/* eslint-disable @typescript-eslint/no-explicit-any */
// react-i18next v17 requires TypeScript 5+; this project is on TS 4.9.5.
// See src/i18n/index.ts for the project-wide workaround and LoginForm
// for the local alias pattern.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLanguage } from '../../contexts/LanguageContext';
import DateField from '../DateField/DateField';
import { useTranslation as useTranslationUntyped } from 'react-i18next';
import { z } from 'zod';
import { getCountryCallingCode, type CountryCode } from 'libphonenumber-js';
import { apiService } from '../../services/api';
import { normalizeApiError } from '../../types/api-errors';
import { queryKeys } from '../../lib/queryKeys';
import {
    useAvailabilitySlotsQuery,
    useBookAppointmentMutation,
    generateIdempotencyKey,
} from '../../hooks/useQuickBook';
import { useBusinessDetailsQuery } from '../../hooks/useBusinessDetails';
import { useToast } from '../../contexts/ToastContext';
import { trackEvent } from '../../lib/analytics';
import type {
    CalendarAvailabilitySlot,
    Category,
    ServiceDetailsResponse,
} from '../../types/api-responses';
import { ServiceType } from '../../types/enums';
import StepProgressDots, { type QuickBookPhase } from './components/StepProgressDots';
import SlotPickerGrid from './components/SlotPickerGrid';
import GenderGlyph from '../GenderGlyph';
import AgeGroupChip from '../AgeGroupChip';
import {
    getRejectionUx,
    LOCK_RELEASE_CODES,
    normalizeRejectionCode,
    SLOT_REJECTIONS,
} from './rejectionMessages';
import s from './QuickBookModal.module.css';

// AB#1796 — Untyped useTranslation alias (TS 4.9.5 / react-i18next v17
// type incompatibility — same pattern as LoginForm.tsx).
const useTranslation = useTranslationUntyped as any;

/**
 * AB#1796 (CR-2026-05-29) — localStorage key for the PDPL soft-consent
 * tooltip. One-shot per device: once dismissed by any means (Esc, outside
 * click, 8s timeout, or any further keystroke), the tooltip never re-arms
 * in this browser. The permanent value cue (phoneCue) keeps carrying the
 * "SMS reminders reduce no-shows" message daily.
 */
const PHONE_CONSENT_TOOLTIP_LS_KEY = 'devanza.quickBook.phoneConsentTooltipSeen';

/**
 * Internal steps — the modal flows through four states even though the
 * visual stepper shows three phases (mockup §C).
 *
 *   1 Category  → phase 1 "Service"  (large catalog → dedicated step)
 *   2 Service   → phase 1 "Service"
 *   3 Slot      → phase 2 "Slot"
 *   4 Confirm   → phase 3 "Confirm"
 */
type Step = 1 | 2 | 3 | 4;

function stepToPhase(step: Step): QuickBookPhase {
    if (step === 1 || step === 2) return 1;
    if (step === 3) return 2;
    return 3;
}

interface QuickBookModalProps {
    branchId: number;
    branchName?: string;
    onClose: () => void;
    onBooked?: () => void;
    /**
     * AB#1752 — Staff-first quick-book entry-point (mockup §D). When present,
     * the modal opens with the staff already selected via right-click on the
     * Calendar grid's staff column header. The Slot step's availability query
     * is constrained to this staff, the gender filter is hidden (irrelevant
     * when staff is fixed), and the stepper grows to 4 phases
     * (Staff / Service / Slot / Confirm) with Staff always "done".
     */
    lockedStaffId?: number;
    lockedStaffName?: string;
    lockedStaffGender?: 'Male' | 'Female' | null;
    /**
     * Click-to-create entry-point — owner clicked an empty cell on the
     * Calendar Day or Week grid. The (staff, date, time) tuple comes from
     * the click position, so the slot picker step is *redundant*: the
     * owner already chose the slot by clicking. In prefill mode the modal:
     *   - Forces `lockedStaffId` / `lockedStaffName` to the prefilled staff
     *   - Skips the Slot step entirely — after picking a service, the modal
     *     synthesizes a CalendarAvailabilitySlot from the prefill and
     *     jumps straight to Confirm
     *   - Shows the prefilled time in the Confirm card
     *
     * The BE still validates the slot on POST — if the staff is off or the
     * window conflicts with an existing booking, the user sees the standard
     * `Slot.CONFLICT` toast and the modal stays open. They can then click a
     * different cell.
     */
    prefillDate?: string;        // "yyyy-MM-dd"
    prefillTime?: string;        // "HH:mm"
}

/** O3 (SO-20) — modal dates in the owner's language, day-first (never en-US). */
const dateLocale = (language: string) => (language === 'ar' ? 'ar' : 'en-GB');

/** O3 (SO-38) — duration units; module helpers take them from the component's t(). */
export interface DurationUnits { min: string; hr: string }
const EN_UNITS: DurationUnits = { min: 'min', hr: 'hr' };

/** Local-date string in yyyy-MM-dd (browser TZ — matches the date input). */
function localTodayIso(): string {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

/** Add N days to a yyyy-MM-dd string. */
function addDaysIso(iso: string, days: number): string {
    const d = new Date(`${iso}T00:00:00`);
    d.setDate(d.getDate() + days);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** "Wed 22" — short label for the day-after-tomorrow chip (mockup §C). */
function shortDayLabel(iso: string, language: string): string {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString(dateLocale(language), { weekday: 'short', day: 'numeric' });
}

/**
 * B28 — Long, unambiguous date for the Confirm card so the staff/owner
 * can verify they're booking on the right day before submitting.
 * Example: "Tuesday, 26 May 2026". Locale-aware via Intl.DateTimeFormat.
 */
function longDateLabel(iso: string, language: string): string {
    const d = new Date(`${iso}T00:00:00`);
    return d.toLocaleDateString(dateLocale(language), {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
}

/**
 * B28 — Humanize an "HH:mm:ss" (or "HH:mm") duration string into "45 min" /
 * "1 hr 30 min" / "2 hr". The raw "00:45:00" format from the BE was being
 * shown verbatim on the Confirm card and the service-pick list, confusing
 * owners who read it as a slot time instead of a duration.
 */
function humanizeDuration(value: string | null | undefined, u: DurationUnits = EN_UNITS): string {
    if (!value) return '';
    const parts = value.split(':');
    if (parts.length < 2) return value;
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    if (Number.isNaN(h) || Number.isNaN(m)) return value;
    if (h === 0 && m === 0) return `0 ${u.min}`;
    if (h === 0) return `${m} ${u.min}`;
    if (m === 0) return `${h} ${u.hr}`;
    return `${h} ${u.hr} ${m} ${u.min}`;
}

/**
 * "HH:mm" + a duration string ("HH:mm" or "HH:mm:ss") → "HH:mm" end time.
 * Used in click-to-create flow so the Confirm card shows a real window
 * for the prefilled slot.
 */
function computeEndTime(start: string, duration: string): string {
    const [sh, sm] = (start ?? '00:00').split(':').map(Number);
    const dParts = (duration ?? '00:30').split(':').map(Number);
    const dh = dParts[0] ?? 0;
    const dm = dParts[1] ?? 30;
    const total = (sh * 60 + sm) + (dh * 60 + dm);
    const eh = Math.floor((total % (24 * 60)) / 60);
    const em = total % 60;
    return `${String(eh).padStart(2, '0')}:${String(em).padStart(2, '0')}`;
}

/**
 * AB#1840 — accepts the BE's string-or-numeric ServiceType. Per Faris F-Q2=A
 * (decision pass 3, 2026-05-31) the BE serializes the enum as a JSON string,
 * but legacy callers still pass numbers.
 */
function serviceTypeLabelKey(t?: number | string | null): string {
    if (t == null) return '';
    if (t === ServiceType.MenOnly || t === 'MenOnly') return 'cal.qb.serviceType.men';
    if (t === ServiceType.WomenOnly || t === 'WomenOnly') return 'cal.qb.serviceType.women';
    if (t === ServiceType.Everyone || t === 'Everyone') return 'cal.qb.serviceType.everyone';
    return '';
}

/**
 * AB#1840 — true when the ServiceType is the open "Everyone" / "Mixed"
 * variant. Tolerates both the string + numeric BE shapes.
 */
function isEveryoneServiceType(t?: number | string | null): boolean {
    return t === ServiceType.Everyone || t === 'Everyone';
}

/**
 * AB#1849 (PBI-K) / AB#1838 — derives the "required walk-in gender" axis
 * from a salon's ServiceType. Returns null for `Everyone` (no chip group
 * is rendered) and the implied gender for the restrictive variants.
 *
 *   - MenOnly   → 'Male'   (only male customers allowed)
 *   - WomenOnly → 'Female' (only female customers allowed)
 *   - Everyone  → null     (chip group hidden — any walk-in OK)
 *
 * Tolerates both the BE's string + numeric ServiceType shapes (Faris F-Q2=A
 * standardised to string, but legacy numeric still drifts in via some
 * controllers).
 */
function salonRequiredCustomerGender(
    t?: number | string | null,
): 'Male' | 'Female' | null {
    if (t === ServiceType.MenOnly || t === 'MenOnly') return 'Male';
    if (t === ServiceType.WomenOnly || t === 'WomenOnly') return 'Female';
    return null;
}

/**
 * AB#1849 (PBI-K) — Zod schema for the walk-in customer-gender axis.
 *
 * Plain `z.enum(['Male', 'Female']).nullable().optional()` covers the
 * shape, but the salon-aware refinement runs in the caller via
 * `validateCustomerGender(salonServiceType, value)` so we can produce the
 * exact i18n error key (`customerGenderRequired` vs `customerGenderMismatch`)
 * rather than a generic Zod issue. This keeps the per-field error copy
 * decoupled from Zod's path/format conventions and lets us reuse the same
 * helper on BE-error fallback.
 */
export const customerGenderSchema = z.enum(['Male', 'Female']).nullable().optional();

/** Returned by `validateCustomerGender` — null = OK; otherwise an i18n key + params. */
export type CustomerGenderError =
    | null
    | { key: 'customerGenderRequired' }
    | { key: 'customerGenderMismatch'; params: { gender: 'Male' | 'Female' } };

/**
 * AB#1849 — Client-side mirror of the BE precondition chain. Returns null
 * when the value is acceptable for the salon's ServiceType; otherwise a
 * tagged error object the caller surfaces via the i18n `t()` function.
 *
 * The two error shapes match the BE error codes exactly:
 *   - `customerGenderRequired` ↔ BE `CUSTOMER_GENDER_REQUIRED`
 *   - `customerGenderMismatch` ↔ BE `CUSTOMER_GENDER_MISMATCHES_SALON`
 */
export function validateCustomerGender(
    salonServiceType: number | string | null | undefined,
    value: 'Male' | 'Female' | null | undefined,
): CustomerGenderError {
    const required = salonRequiredCustomerGender(salonServiceType);
    if (required == null) return null; // Everyone — anything (incl. null) is OK
    if (value == null) return { key: 'customerGenderRequired' };
    if (value !== required) {
        return { key: 'customerGenderMismatch', params: { gender: required } };
    }
    return null;
}

function formatDurationRange(min: string, max: string, u: DurationUnits = EN_UNITS): string {
    // B28 — Humanize HH:MM:SS to "45 min" / "1 hr 30 min" / "2 hr" so the
    // duration reads clearly instead of looking like a slot time.
    const minH = humanizeDuration(min, u);
    const maxH = humanizeDuration(max, u);
    return minH === maxH ? minH : `${minH} – ${maxH}`;
}

/**
 * Build the phone-input placeholder from the business's ISO-2 country code
 * (e.g. "AE" → "+971 …"). Falls back to a generic "+… " when the country
 * isn't loaded yet or libphonenumber-js doesn't recognise the ISO-2.
 *
 * Previously this was hardcoded to "+962 …" which was misleading for
 * UAE-resident salons (the larger launch market) — the dial code now
 * tracks the salon's actual country instead of a fixed default.
 */
function phonePlaceholderForCountry(iso2: string | null | undefined): string {
    if (!iso2 || iso2.length !== 2) return '+… ';
    try {
        const code = getCountryCallingCode(iso2.toUpperCase() as CountryCode);
        return `+${code} …`;
    } catch {
        return '+… ';
    }
}

function formatPriceRange(pricing: ServiceDetailsResponse['pricingOptions']): string {
    if (!pricing || pricing.length === 0) return '';
    const prices = pricing.map(p => p.price).filter(p => typeof p === 'number');
    if (prices.length === 0) return '';
    const min = Math.min(...prices);
    const max = Math.max(...prices);
    const currency = pricing[0].currency || '';
    return min === max
        ? `${min} ${currency}`.trim()
        : `${min}–${max} ${currency}`.trim();
}

/**
 * AB#1751 — Service-first quick-book modal. 3 steps:
 *   1. Pick category
 *   2. Pick service in that category
 *   3. Pick a slot (with date / around / gender filters) → confirm
 *
 * Idempotency: a fresh key is generated on mount and reused on retry —
 * the BE returns `isReplay=true` if the same key is sent twice. Modal
 * stays open on `Slot.CONFLICT` so the user can re-search; closes on
 * happy-path success.
 *
 * Hardcoded EN strings per the deferred-i18n-batch convention used
 * across `/my-business` (CR-2026-05-12-v2, AB#1713) and the C8 / C3
 * FE PRs. AR translations land in the batch PBI.
 */
const QuickBookModal: React.FC<QuickBookModalProps> = ({
    branchId, branchName, onClose, onBooked,
    lockedStaffId, lockedStaffName, lockedStaffGender,
    prefillDate, prefillTime,
}) => {
    const isStaffLocked = lockedStaffId != null;
    const isPrefillMode = !!(lockedStaffId && prefillDate && prefillTime);
    const { showToast } = useToast();
    const [step, setStep] = useState<Step>(1);

    // B5 — when the BE rejects with a staff-property error (capability or
    // gender mismatch), keeping the staff lock would dead-end the picker:
    // "no slots for Huda" + "try a different date" — but a different date
    // won't fix it because Huda lacks the capability forever. Set this flag
    // to drop the staff filter in the picker so the owner sees OTHER staff
    // at the branch who CAN perform the service. Cleared whenever the user
    // picks a fresh service (a new service might be fine with locked staff).
    // Date errors (OUTSIDE_SHIFT, BRANCH_CLOSED) and conflict errors
    // (CONFLICT) keep the lock — date IS the fix in those cases.
    const [staffLockOverridden, setStaffLockOverridden] = useState(false);
    // B26 + audit Phase A — Track the FULL BE rejection code that
    // triggered the lock-release. The picker banner + empty state read
    // this through `getRejectionUx(code, ctx)` so the three surfaces
    // (toast at top, banner above the tree, empty state inside the
    // tree) stay in lockstep. Cleared whenever a fresh service is
    // picked, since the new service might be fine with the locked
    // staff. Stored as `string | null` not the narrow union so future
    // BE codes don't require a type change here — the helper handles
    // unknown codes with a generic fallback.
    const [lockReleaseCode, setLockReleaseCode] = useState<string | null>(null);
    const effectiveStaffLocked = isStaffLocked && !staffLockOverridden;

    // Selections
    const [categoryId, setCategoryId] = useState<number | null>(null);
    const [serviceId, setServiceId] = useState<number | null>(null);
    const [selectedService, setSelectedService] = useState<ServiceDetailsResponse | null>(null);
    const [selectedSlot, setSelectedSlot] = useState<CalendarAvailabilitySlot | null>(null);

    // Step-3 filters.
    // B23 — When the modal opens from a cell click (prefillDate +
    // prefillTime), seed BOTH the date AND around-time so the slot
    // picker centers the candidate grid on what the owner clicked.
    // Previously `around` defaulted to '' and the slot list showed
    // slots from 09:00 even when the click was at 14:30, which
    // looked like the cell context was being ignored.
    const [date, setDate] = useState<string>(() => prefillDate || localTodayIso());
    const [around, setAround] = useState<string>(() => prefillTime || ''); // "HH:mm" or empty
    const [genderFilter, setGenderFilter] = useState<number | null>(null); // 1=Male, 2=Female

    // Customer fields (collected during slot confirm)
    const [customerName, setCustomerName] = useState('');
    const [customerPhone, setCustomerPhone] = useState('');
    // AB#1849 (PBI-K) — Walk-in customer gender. Pre-seeded below from the
    // salon's ServiceType once businessDetails resolves. Stays `null` for
    // Everyone salons (chip group is hidden in that case). Owner can re-pick
    // only if the salon is Everyone — restrictive salons grey out the
    // opposite chip with a tooltip explaining why.
    const [customerGender, setCustomerGender] = useState<'Male' | 'Female' | null>(null);
    // Client-side error key + params for the chip group. Cleared when the
    // owner picks any value. BE rejections (CUSTOMER_GENDER_REQUIRED /
    // CUSTOMER_GENDER_MISMATCHES_SALON) feed into this same slot so the
    // error region is unified between client + server.
    const [customerGenderError, setCustomerGenderError] = useState<CustomerGenderError>(null);

    // AB#1796 (CR-2026-05-29) — Translation hook for the Calendar i18n
    // namespace (Quick-Book Confirm copy). Other QuickBookModal strings
    // remain hardcoded pending the broader i18n batch; only the new copy
    // introduced by this CR routes through `t()` to satisfy AC-6.
    const { t } = useTranslation('calendar');
    // O3 (SO-38) — the rest of the modal copy (owner string module, i18n/owner/shell.ts).
    const { t: tl, language } = useLanguage();
    const units: DurationUnits = { min: tl('cal.qb.unit.min'), hr: tl('cal.qb.unit.hr') };

    // AB#1796 — PDPL soft-consent tooltip state. The tooltip arms only
    // when the user types the first non-whitespace character into the
    // phone field. It dismisses on Esc, outside click, 8s timeout, or
    // any further keystroke. Once dismissed by any means it persists to
    // localStorage and never re-arms in this browser (one-shot per
    // device).
    const [phoneConsentTooltipOpen, setPhoneConsentTooltipOpen] = useState(false);
    const phoneConsentSeenRef = useRef<boolean>(
        typeof window !== 'undefined'
            && window.localStorage?.getItem(PHONE_CONSENT_TOOLTIP_LS_KEY) === 'true',
    );
    const dismissPhoneConsentTooltip = React.useCallback(() => {
        setPhoneConsentTooltipOpen(false);
        if (!phoneConsentSeenRef.current) {
            phoneConsentSeenRef.current = true;
            try {
                window.localStorage?.setItem(PHONE_CONSENT_TOOLTIP_LS_KEY, 'true');
            } catch {
                // localStorage quota exceeded / disabled — safe to swallow;
                // the worst case is the tooltip arms again next session.
            }
        }
    }, []);

    // 8s auto-dismiss timer + Esc key handler when the tooltip is open.
    useEffect(() => {
        if (!phoneConsentTooltipOpen) return;
        const timer = window.setTimeout(dismissPhoneConsentTooltip, 8000);
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') dismissPhoneConsentTooltip();
        };
        window.addEventListener('keydown', onKey);
        return () => {
            window.clearTimeout(timer);
            window.removeEventListener('keydown', onKey);
        };
    }, [phoneConsentTooltipOpen, dismissPhoneConsentTooltip]);

    // Idempotency key — stable across the modal's lifetime.
    const [idempotencyKey] = useState(() => generateIdempotencyKey());

    // Business country drives the phone-input dial-code placeholder.
    // useBusinessDetailsQuery is cached app-wide (60s staleTime); reuse is
    // free. Falls back gracefully when the data hasn't loaded yet.
    const { data: businessDetails } = useBusinessDetailsQuery();
    const phonePlaceholder = useMemo(
        () => phonePlaceholderForCountry(businessDetails?.countryIso2),
        [businessDetails?.countryIso2],
    );

    // AB#1849 (PBI-K) — Required walk-in customer gender for this salon.
    // null = Everyone salon → chip group HIDDEN (matches PBI-K AC-4).
    // 'Male' = MenOnly → chip group shown, Male pre-selected + locked.
    // 'Female' = WomenOnly → chip group shown, Female pre-selected + locked.
    const salonRequiredGender = useMemo(
        () => salonRequiredCustomerGender(businessDetails?.serviceType),
        [businessDetails?.serviceType],
    );
    const showCustomerGenderChips = salonRequiredGender != null;

    // Pre-select the salon's required gender once the business resolves.
    // Only seeds when the field is still `null` so an in-flight owner edit
    // isn't overwritten by a stale fetch. Re-seeds if the businessDetails
    // serviceType later changes (e.g. owner updates the setting in another tab
    // and the query refetches) — `customerGender` reverts to the new default.
    useEffect(() => {
        if (salonRequiredGender == null) {
            // Everyone salon: never carry an unrelated value from a prior
            // restrictive salon state. Reset to null so the BE receives null.
            setCustomerGender(null);
            setCustomerGenderError(null);
            return;
        }
        setCustomerGender(salonRequiredGender);
        setCustomerGenderError(null);
    }, [salonRequiredGender]);

    /* ── Data fetches ──────────────────────────────────────────── */

    const { data: categoriesData, isLoading: catsLoading } = useQuery({
        queryKey: queryKeys.categories.business(),
        queryFn: () => apiService.getBusinessCategories(),
        staleTime: 60_000,
    });
    const categories = useMemo<Category[]>(
        () => (categoriesData?.categories ?? []).filter(c => c.isActive),
        [categoriesData]);

    const { data: servicesData, isLoading: svcsLoading } = useQuery({
        queryKey: queryKeys.services.list(branchId),
        queryFn: () => apiService.getOwnerServices(),
        staleTime: 60_000,
    });
    const services = useMemo<ServiceDetailsResponse[]>(() => {
        const all = (servicesData?.services ?? []).filter(svc => svc.isActive);
        if (categoryId == null) return [];
        // The owner services endpoint returns services across the business.
        // Filter to the chosen category. (BE-side `branchId` filter would be
        // ideal, but the existing endpoint returns all — we narrow client-
        // side using service.branchId.)
        let filtered = all.filter(svc =>
            svc.branchId === branchId
            // categoryName is the row's category label; matching by name
            // works for the v1 surface since each business has unique cat
            // names. The future-correct path is to filter by categoryId
            // once the service DTO exposes it.
            && svc.categoryName === categories.find(c => c.id === categoryId)?.name);

        // AB#1752 — Staff-first mode: filter to services this staff can
        // perform. Mirrors the BE engine's logic (CR-2026-05-15-v3):
        // a service is performable by this staff if EITHER its
        // `assignedStaffIds` is empty (implicit-all default — any staff at
        // the branch can do it) OR the staff is explicitly in the list.
        if (isStaffLocked) {
            filtered = filtered.filter(svc => {
                const assigned = svc.assignedStaffIds ?? [];
                return assigned.length === 0 || assigned.includes(lockedStaffId as number);
            });
        }
        return filtered;
    }, [servicesData, categoryId, branchId, categories, isStaffLocked, lockedStaffId]);

    // Availability — fires once we're on step 3 with serviceId set.
    // Staff-first flow (mockup §D) constrains the search to a single staff
    // via `lockedStaffId`; the gender filter is hidden in that mode so we
    // pass null for staffGender to keep the cache key clean.
    const availability = useAvailabilitySlotsQuery({
        branchId,
        serviceId: serviceId ?? undefined,
        from: step === 3 ? date : undefined,
        around: around || null,
        // Use the override-aware flag so STAFF_CANT_SERVICE / GENDER_MISMATCH
        // fallback can broaden the search to every eligible staff at the
        // branch instead of dead-ending on the originally-locked staff.
        staffId: effectiveStaffLocked ? lockedStaffId : null,
        staffGender: effectiveStaffLocked ? null : genderFilter,
        take: 15,
    });

    const bookMutation = useBookAppointmentMutation();

    /* ── Step transitions ──────────────────────────────────────── */

    function pickCategory(id: number) {
        setCategoryId(id);
        setServiceId(null);
        setSelectedService(null);
        setStep(2);
    }

    function pickService(svc: ServiceDetailsResponse) {
        setServiceId(svc.id);
        setSelectedService(svc);
        setSelectedSlot(null);
        // Picking a fresh service means the previous capability/gender
        // rejection no longer applies — restore the staff lock so the
        // owner's original intent (book WITH that staff) stays in force
        // until/unless this new service also gets rejected.
        setStaffLockOverridden(false);
        setLockReleaseCode(null);

        // Click-to-create: owner clicked a specific (staff, date, time) on the
        // calendar grid; synthesise a slot from the prefill and skip the slot
        // picker. End-time derives from the service's min duration so the
        // Confirm card displays a real window. The BE re-validates the slot
        // on POST — if the staff is off or there's a conflict, the standard
        // Slot.CONFLICT toast fires and we drop back to the slot step so the
        // owner can pick another time.
        if (isPrefillMode && lockedStaffId && prefillDate && prefillTime) {
            const endTime = computeEndTime(prefillTime, svc.minDuration);
            const synth: CalendarAvailabilitySlot = {
                staffId: lockedStaffId,
                staffDisplayName: lockedStaffName ?? '',
                staffGender: lockedStaffGender ?? null,
                date: prefillDate,
                startTime: prefillTime,
                endTime,
            };
            setSelectedSlot(synth);
            setStep(4);
        } else {
            setStep(3);
        }
    }

    function back() {
        if (step === 4) {
            // In prefill mode, back from Confirm returns to the service
            // picker (step 2) — the slot step (3) was bypassed so there's
            // no point landing on it.
            setStep(isPrefillMode ? 2 : 3);
            if (isPrefillMode) setSelectedSlot(null);
        } else if (step === 3) {
            setSelectedSlot(null);
            setStep(2);
        } else if (step === 2) {
            setCategoryId(null);
            setServiceId(null);
            setStep(1);
        }
    }

    function continueToConfirm() {
        if (!selectedSlot) return;
        setStep(4);
    }

    async function confirmBook() {
        if (!selectedSlot || !selectedService || branchId == null) return;

        // AB#1849 (PBI-K) — Client-side gender precondition mirrors the BE
        // chain. Block submit early so the owner doesn't pay the round-trip
        // for an obvious mismatch; the BE re-checks regardless (defense-
        // in-depth). For Everyone salons this is always a no-op.
        const localGenderErr = validateCustomerGender(
            businessDetails?.serviceType,
            customerGender,
        );
        if (localGenderErr) {
            setCustomerGenderError(localGenderErr);
            return;
        }
        setCustomerGenderError(null);

        try {
            // AB#1796 (CR-2026-05-29) AC-4 — Walk-in row persistence.
            // When both name and phone are blank at submit, send the
            // sentinel "Walk-in" as customerName so the appointment row
            // is findable in later queries (e.g. "who was the 11:00 on
            // Friday?") instead of NULL. Phone stays NULL when blank —
            // no fake "0000000000" placeholder. This matches the in-
            // modal placeholder semantic the owner already sees.
            const trimmedName = customerName.trim();
            const trimmedPhone = customerPhone.trim();
            const submittedName: string = trimmedName.length > 0 ? trimmedName : 'Walk-in';
            // AB#1849 (PBI-K) — Wire the gender into the request body. For
            // Everyone salons the value is null (chip group is hidden); BE
            // accepts null in that case. For MenOnly / WomenOnly the value
            // is pre-seeded + validated above, so it's always a literal
            // 'Male' / 'Female' string by the time we get here.
            const result = await bookMutation.mutateAsync({
                body: {
                    branchId,
                    serviceId: selectedService.id,
                    staffId: selectedSlot.staffId,
                    date: selectedSlot.date,
                    startTime: selectedSlot.startTime,
                    customerName: submittedName,
                    customerPhone: trimmedPhone.length > 0 ? trimmedPhone : null,
                    customerGender: customerGender ?? null,
                },
                idempotencyKey,
            });
            // AB#1796 (CR-2026-05-29) — Quick-Book completion analytics.
            // Fire-and-forget per AC-3: telemetry failures must not block
            // the modal close or surface an error. trackEvent itself is
            // no-op-safe, but wrap in try/catch as belt-and-suspenders so
            // future replacements of the scaffold can't accidentally
            // throw into the booking success path. No PII — only the IDs
            // already in scope plus two booleans.
            try {
                const businessIdForEvent = businessDetails?.id ?? 0;
                trackEvent('quickBook.completed', {
                    businessId: businessIdForEvent,
                    branchId,
                    serviceId: selectedService.id,
                    staffId: selectedSlot.staffId,
                    // Reuse the trimmed values computed above. withName
                    // reflects whether the OWNER typed a real name — the
                    // "Walk-in" sentinel substitution at the request body
                    // does NOT flip withName to true (otherwise the
                    // funnel metric would be useless).
                    withPhone: trimmedPhone.length > 0,
                    withName: trimmedName.length > 0,
                });
            } catch {
                // Swallow — telemetry must never break booking UX.
            }
            showToast(
                result.isReplay
                    ? tl('cal.qb.toast.exists')
                    : tl('cal.qb.toast.confirmed'),
                'success');
            onBooked?.();
            onClose();
        } catch (err: unknown) {
            // B11 — Three error shapes can come back from the BE:
            //   1. New Result<Error> shape — { code: "Slot.X", message }
            //   2. Legacy FluentValidation middleware — { errors: { Field: [msg] },
            //      message: "Validation failed", errorCode: "VALIDATION_ERROR" }
            //   3. Anything else (network drop, 500) — axios's default
            //      "Request failed with status code N" which is useless to the user
            // AB#1919 (WS-5b) — read through the single normalizer so the
            // rejection `code` is resolved via `errorCode ?? code` (§3). UNKNOWN
            // (no code present) collapses to `undefined` so the existing
            // `code ? …` guards fall through to the generic toast unchanged.
            const appError = normalizeApiError(err);
            // AB#1920 (WS-1b.1) — DUAL-RECOGNITION. The BE is flipping these
            // booking literals to canonical registry codes (PR-J0). Fold the
            // incoming code onto its LEGACY spelling so every comparison below
            // — the two CUSTOMER_GENDER strict-equality checks AND the
            // getRejectionUx / SLOT_REJECTIONS / LOCK_RELEASE_CODES lookups —
            // recognises BOTH the legacy literal and the canonical code and
            // produces identical UX. Legacy passes through unchanged, so the
            // pre-flip behaviour is untouched (additive). See rejectionMessages.ts.
            const rawCode = appError.code !== 'UNKNOWN' ? appError.code : undefined;
            const code = rawCode ? normalizeRejectionCode(rawCode) : undefined;

            // AB#1849 (PBI-K) — Walk-in customer-gender BE rejections route
            // to the modal's inline error region rather than the toast +
            // slot-picker dropback flow, because the only remedy is for the
            // owner to flip a chip on the Confirm step. No need to re-query
            // availability or release the staff lock. Stay on step 4.
            // (Legacy `CUSTOMER_GENDER_REQUIRED` and canonical
            // `CUSTOMER.GENDER_REQUIRED` both fold to the legacy literal here.)
            if (code === 'CUSTOMER_GENDER_REQUIRED') {
                setCustomerGenderError({ key: 'customerGenderRequired' });
                return;
            }
            if (code === 'CUSTOMER_GENDER_MISMATCHES_SALON') {
                const expected = salonRequiredCustomerGender(businessDetails?.serviceType)
                    ?? (customerGender === 'Male' ? 'Female' : 'Male'); // defensive fallback
                setCustomerGenderError({
                    key: 'customerGenderMismatch',
                    params: { gender: expected },
                });
                return;
            }

            // Audit Phase A — Single dispatch through getRejectionUx so the
            // toast, the picker banner, and the empty state all read from
            // one matrix. Previously each surface had its own switch and
            // they drifted (B26 banner copy bug, B30 empty-state bug, G1
            // STAFF_NOT_AT_BRANCH never-released-lock bug — all symptoms
            // of three switches that were edited independently). The
            // matrix lives in rejectionMessages.ts and is anchored by
            // the audit doc at devanza-claude/docs/audits/calendar-v1-
            // booking-audit.md Part A.3.
            const staffName = lockedStaffName ?? selectedSlot.staffDisplayName ?? 'Staff';
            const ux = code
                ? getRejectionUx(code, {
                    staffName,
                    serviceName: selectedService.name,
                    branchName,
                    around: around || null,
                }, tl)
                : null;

            let msg: string;
            if (ux) {
                msg = ux.toast;
            } else {
                // Fall back through the legacy FluentValidation middleware
                // shape (B11) before bottoming out on axios's useless
                // "Request failed with status code N". The middleware
                // emits `errors: { Date: ["Cannot book in the past."] }`
                // and similar field-message structures which are far
                // more actionable than a generic toast.
                // AB#1919 (WS-5b) — field errors + the human message both come
                // off the canonical AppError now (`detail` = `detail ?? message`).
                const firstFieldMessage = appError.errors
                    ? Object.values(appError.errors)
                        .map(arr => (Array.isArray(arr) ? arr[0] : null))
                        .find(m => typeof m === 'string' && m.length > 0)
                    : undefined;
                if (firstFieldMessage) {
                    msg = firstFieldMessage as string;
                } else if (appError.detail && appError.detail !== 'Validation failed') {
                    msg = appError.detail;
                } else {
                    msg = tl('cal.qb.toast.failed');
                }
            }
            showToast(msg, 'error');
            // Audit Phase A — SLOT_REJECTIONS / LOCK_RELEASE_CODES live in
            // rejectionMessages.ts so adding a new code on the BE side is a
            // single-file edit on the FE. Lock-release semantics per code:
            //   STAFF_CANT_SERVICE / GENDER_MISMATCH / OUTSIDE_SHIFT /
            //   STAFF_NOT_AT_BRANCH → release (different staff is the answer).
            //   CONFLICT / BRANCH_CLOSED → keep lock (different time/date is
            //                              the answer; staff isn't the blocker).
            // G1 closed by adding STAFF_NOT_AT_BRANCH to the release set.
            if (code && SLOT_REJECTIONS.has(code)) {
                if (LOCK_RELEASE_CODES.has(code)) {
                    setStaffLockOverridden(true);
                    setLockReleaseCode(code);
                }
                if (isPrefillMode) {
                    setSelectedSlot(null);
                    // B23 — Preserve both the clicked date AND time so the
                    // slot picker centers on what the owner originally
                    // clicked. Without this, the time chip reset to "Any"
                    // and the candidate list started at 09:00 regardless
                    // of whether the click was at 14:30 — that's what
                    // looked like "wrong list of times" in UAT.
                    if (prefillDate) setDate(prefillDate);
                    if (prefillTime) setAround(prefillTime);
                    setStep(3);
                } else {
                    // Re-fetch the candidate list so the user sees current slots.
                    availability.refetch();
                    setSelectedSlot(null);
                }
            }
        }
    }

    /* ── Step render ───────────────────────────────────────────── */

    // Step 3 — only show the gender filter when the service is for Everyone
    // (gender filters are nonsensical for WomenOnly / MenOnly services since
    // the BE rejects mismatched genders up-front).
    const showGenderFilter = isEveryoneServiceType(selectedService?.serviceType);
    const selectedSlotKey = selectedSlot ? `${selectedSlot.staffId}_${selectedSlot.startTime}` : null;

    return (
        <div className={s.backdrop} role="presentation" onClick={onClose}>
            <div
                className={s.modal}
                role="dialog"
                aria-modal="true"
                aria-label={tl('cal.qb.dialog')}
                onClick={e => e.stopPropagation()}
            >
                <div className={s.header}>
                    <div className={s.headerLeft}>
                        <h2 className={s.title}>
                            {/* B12 — title polish: once the staff lock has
                                been dropped (STAFF_CANT_SERVICE override),
                                stop pretending we're still booking the
                                originally-clicked staff. Switch to a
                                service-scoped title so the header matches
                                what the picker is actually showing. */}
                            {staffLockOverridden && selectedService
                                ? tl('cal.qb.title.pickTime').replace('{service}', selectedService.name)
                                : isPrefillMode
                                    ? tl('cal.qb.title.bookAt').replace('{name}', lockedStaffName ?? '').replace('{time}', prefillTime ?? '')
                                    : isStaffLocked
                                        ? tl('cal.qb.title.bookWith').replace('{name}', lockedStaffName ?? '')
                                        : tl('cal.qb.title.quickBook')}
                        </h2>
                        {branchName && (
                            <span className={s.subtitle}>{tl('cal.qb.atBranch').replace('{branch}', branchName)}</span>
                        )}
                    </div>
                    <button
                        type="button"
                        className={s.closeBtn}
                        onClick={onClose}
                        aria-label={tl('cal.qb.close')}
                    >
                        ×
                    </button>
                </div>

                {/* Stepper — mockup §C (3 phases) or §D (4 phases, Staff prepended). */}
                <StepProgressDots
                    phase={stepToPhase(step)}
                    withStaffPhase={isStaffLocked}
                />

                <div className={s.body}>
                    {/* Step 1 — pick category */}
                    {step === 1 && (
                        <>
                            <h3 className={s.stepTitle}>{tl('cal.qb.step.category')}</h3>
                            {catsLoading && <div className={s.placeholderState}>{tl('cal.qb.loadingCategories')}</div>}
                            {!catsLoading && categories.length === 0 && (
                                <div className={s.placeholderState}>
                                    {tl('cal.qb.noCategories')}
                                </div>
                            )}
                            {!catsLoading && categories.map(c => (
                                <button
                                    key={c.id}
                                    type="button"
                                    className={s.listItem}
                                    onClick={() => pickCategory(c.id)}
                                >
                                    <span className={s.listItemTitle}>{c.name}</span>
                                </button>
                            ))}
                        </>
                    )}

                    {/* Step 2 — pick service */}
                    {step === 2 && (
                        <>
                            <h3 className={s.stepTitle}>{tl('cal.qb.step.service')}</h3>
                            {svcsLoading && <div className={s.placeholderState}>{tl('cal.qb.loadingServices')}</div>}
                            {!svcsLoading && services.length === 0 && (
                                <div className={s.placeholderState}>
                                    {isStaffLocked
                                        ? tl('cal.qb.staffNoServices').replace('{name}', lockedStaffName ?? tl('cal.qb.thisStaff'))
                                        : tl('cal.qb.noServices')}
                                </div>
                            )}
                            {!svcsLoading && services.map(svc => (
                                <button
                                    key={svc.id}
                                    type="button"
                                    className={s.listItem}
                                    onClick={() => pickService(svc)}
                                >
                                    <div>
                                        <div className={s.listItemTitle}>{svc.name}</div>
                                        <div className={s.listItemMeta}>
                                            <span>{formatDurationRange(svc.minDuration, svc.maxDuration, units)}</span>
                                            {/* AB#1840 (PBI-B) / AB#1838 — per-service gender chip.
                                                Replaces the legacy "serviceTypePill" inline span. Per
                                                founder Q6=A (2026-05-31) we now render the Mixed/Everyone
                                                chip explicitly instead of hiding it. */}
                                            <GenderGlyph serviceType={svc.serviceType} />
                                            {/* AB#1847 (PBI-I) / AB#1838 — per-service age-group chip.
                                                Adult is the silent default (no chip). Kid + Both surface
                                                next to the GenderGlyph so customers can spot kid-friendly
                                                services at a glance. AR `Both` = `كلاهما` per founder Q1. */}
                                            <AgeGroupChip ageGroup={svc.ageGroup} />
                                        </div>
                                    </div>
                                    <span className={s.servicePrice}>
                                        {formatPriceRange(svc.pricingOptions)}
                                    </span>
                                </button>
                            ))}
                        </>
                    )}

                    {/* Step 3 — slot picker + filters + confirm */}
                    {step === 3 && selectedService && (
                        <>
                            <h3 className={s.stepTitle}>{tl('cal.qb.step.slot').replace('{service}', selectedService.name)}</h3>

                            {/* Service summary card (mockup §C lines 1387-1395). */}
                            <div className={s.confirmCard}>
                                <h5 className={s.confirmCardLabel}>{tl('cal.qb.service')}</h5>
                                <div className={s.confirmCardRow}>
                                    <span className={s.confirmKey}>{selectedService.name}</span>
                                    <span className={s.confirmVal}>
                                        {formatDurationRange(selectedService.minDuration, selectedService.maxDuration, units)}
                                        {' · '}
                                        {formatPriceRange(selectedService.pricingOptions)}
                                    </span>
                                </div>
                                {(() => {
                                    // Only render the Service-type row when there's a real
                                    // label to show. Filter out `Everyone` (implicit; no badge)
                                    // and any service that didn't carry a serviceType (legacy
                                    // rows pre-v2 enum or null FK).
                                    const stKey = serviceTypeLabelKey(selectedService.serviceType);
                                    const stLabel = stKey ? tl(stKey) : '';
                                    if (!stLabel || isEveryoneServiceType(selectedService.serviceType)) return null;
                                    return (
                                        <div className={s.confirmCardRowFaint}>
                                            <span className={s.confirmKey}>{tl('cal.qb.serviceTypeLabel')}</span>
                                            <span className={s.confirmVal}>{stLabel}</span>
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* When — date chip row (mockup §C lines 1396-1410). */}
                            <div className={s.filterRow}>
                                <span className={s.filterRowLabel}>{tl('cal.qb.when')}</span>
                                <div className={s.chipGroup}>
                                    {(() => {
                                        const today = localTodayIso();
                                        const d1 = addDaysIso(today, 1);
                                        const d2 = addDaysIso(today, 2);
                                        return (
                                            <>
                                                <button type="button" className={`${s.chip} ${date === today ? s.chipOn : ''}`} onClick={() => setDate(today)}>{tl('cal.today')}</button>
                                                <button type="button" className={`${s.chip} ${date === d1 ? s.chipOn : ''}`} onClick={() => setDate(d1)}>{tl('cal.qb.tomorrow')}</button>
                                                <button type="button" className={`${s.chip} ${date === d2 ? s.chipOn : ''}`} onClick={() => setDate(d2)}>{shortDayLabel(d2, language)}</button>
                                            </>
                                        );
                                    })()}
                                </div>
                                <DateField
                                    className={s.chipDateInput}
                                    value={date}
                                    onChange={iso => setDate(iso || localTodayIso())}
                                    label={tl('cal.qb.pickDate')}
                                />
                            </div>

                            {/* Time-of-day — chip row (mockup §C lines 1412-1432). */}
                            <div className={s.filterRow}>
                                <span className={s.filterRowLabel}>{tl('cal.qb.time')}</span>
                                <div className={s.chipGroup}>
                                    <button type="button" className={`${s.chip} ${around === '' ? s.chipOn : ''}`} onClick={() => setAround('')}>{tl('cal.qb.any')}</button>
                                    <button type="button" className={`${s.chip} ${around === '09:00' ? s.chipOn : ''}`} onClick={() => setAround('09:00')}>{tl('cal.qb.morning')}</button>
                                    <button type="button" className={`${s.chip} ${around === '14:00' ? s.chipOn : ''}`} onClick={() => setAround('14:00')}>{tl('cal.qb.afternoon')}</button>
                                    <button type="button" className={`${s.chip} ${around === '18:00' ? s.chipOn : ''}`} onClick={() => setAround('18:00')}>{tl('cal.qb.evening')}</button>
                                </div>
                                <span className={s.chipTime}>
                                    {tl('cal.qb.specific')}
                                    <input
                                        type="time"
                                        value={around}
                                        onChange={e => setAround(e.target.value)}
                                        aria-label={tl('cal.qb.specificTime')}
                                    />
                                </span>
                            </div>

                            {/* Staff gender — chip row.
                                Hidden in staff-first mode (mockup §D) because
                                the staff is already locked. Otherwise only
                                shown when the service is for Everyone. */}
                            {!isStaffLocked && showGenderFilter && (
                                <div className={s.filterRow}>
                                    <span className={s.filterRowLabel}>{tl('cal.qb.staff')}</span>
                                    <div className={s.chipGroup}>
                                        <button type="button" className={`${s.chip} ${genderFilter == null ? s.chipOn : ''}`} onClick={() => setGenderFilter(null)}>{tl('cal.qb.any')}</button>
                                        <button type="button" className={`${s.chip} ${genderFilter === 2 ? s.chipOn : ''}`} onClick={() => setGenderFilter(2)}>♀ {tl('cal.qb.female')}</button>
                                        <button type="button" className={`${s.chip} ${genderFilter === 1 ? s.chipOn : ''}`} onClick={() => setGenderFilter(1)}>♂ {tl('cal.qb.male')}</button>
                                    </div>
                                    <span className={s.serviceTypeNote}>{tl('cal.qb.bothGenders')}</span>
                                </div>
                            )}

                            {/* Staff-locked indicator (mockup §D) — replaces
                                the gender chip row with a fixed-staff badge.
                                Hidden when the lock has been overridden by a
                                staff-property error so the picker reflects
                                the broadened search. */}
                            {effectiveStaffLocked && (
                                <div className={s.filterRow}>
                                    <span className={s.filterRowLabel}>{tl('cal.qb.staff')}</span>
                                    <span className={s.lockedStaffBadge}>
                                        <span className={s.lockedStaffAvatar} aria-hidden="true">
                                            {(lockedStaffName ?? '?').split(/\s+/).slice(0, 2).map(p => p.charAt(0).toUpperCase()).join('')}
                                        </span>
                                        {lockedStaffName}
                                        {lockedStaffGender === 'Female' && (
                                            <span className={`${s.slotGenderGlyph} ${s.slotGenderF}`} aria-label={tl('cal.qb.female')}>♀</span>
                                        )}
                                        {lockedStaffGender === 'Male' && (
                                            <span className={`${s.slotGenderGlyph} ${s.slotGenderM}`} aria-label={tl('cal.qb.male')}>♂</span>
                                        )}
                                    </span>
                                </div>
                            )}

                            {/* B5 + B26 + audit Phase A — Banner copy comes
                                from the same getRejectionUx helper that
                                produced the toast + the empty state.
                                Single source of truth for all three
                                surfaces — the matrix lives in
                                rejectionMessages.ts. */}
                            {staffLockOverridden && lockedStaffName && lockReleaseCode && (() => {
                                const ux = getRejectionUx(lockReleaseCode, {
                                    staffName: lockedStaffName,
                                    serviceName: selectedService?.name,
                                    branchName,
                                    around: around || null,
                                }, tl);
                                return ux.banner ? (
                                    <div className={s.scoping} role="status">{ux.banner}</div>
                                ) : null;
                            })()}

                            {/* Scoping line — mockup §C lines 1434-1437 / §D
                                lines 1601-1603. Mentions the locked staff
                                when present so the owner sees the filter is
                                pinned. */}
                            <div className={s.scoping}>
                                {(effectiveStaffLocked
                                    ? tl('cal.qb.scope.staff').replace('{name}', lockedStaffName ?? '')
                                    : tl('cal.qb.scope.all'))
                                    .replace('{when}', around ? tl('cal.qb.scope.around').replace('{time}', around) : tl('cal.qb.scope.anyTime'))
                                    .replace('{date}', shortDayLabel(date, language))}
                            </div>

                            {availability.isError && (
                                <div className={s.errorBox} role="alert">
                                    {tl('cal.qb.slotsFailed')}
                                </div>
                            )}

                            {availability.isLoading && (
                                <div className={s.placeholderState}>{tl('cal.qb.findingSlots')}</div>
                            )}

                            {availability.data && !availability.isLoading && (
                                <SlotPickerGrid
                                    slots={availability.data.slots}
                                    isClosestMatch={availability.data.isClosestMatch}
                                    around={around || null}
                                    selectedSlotKey={selectedSlotKey}
                                    onSelect={setSelectedSlot}
                                    // Pass the locked-staff name only while
                                    // the lock is still in force. After a
                                    // capability override the empty state
                                    // shouldn't say "no slots for Huda" —
                                    // we've broadened the search.
                                    lockedStaffName={effectiveStaffLocked ? lockedStaffName : undefined}
                                    // B30 + audit Phase A — Hand the BE
                                    // rejection code straight to the picker;
                                    // SlotPickerGrid calls getRejectionUx
                                    // and renders the matching banner +
                                    // empty state. Single source of truth.
                                    lockReleaseCode={staffLockOverridden ? lockReleaseCode : null}
                                    releasedFromStaffName={staffLockOverridden ? lockedStaffName : undefined}
                                    serviceName={selectedService?.name}
                                    branchName={branchName}
                                />
                            )}
                        </>
                    )}

                    {/* Step 4 — Confirm (mockup §C phase 3). Summary card +
                        customer-name/phone form. Book button lives in the
                        footer like the mockup's "Continue to confirm →" +
                        "Book" two-step CTA pattern. */}
                    {step === 4 && selectedService && selectedSlot && (
                        <>
                            <h3 className={s.stepTitle}>{tl('cal.qb.step.confirm')}</h3>

                            {/* B28 — Confirm card restructured for clarity.
                                Duration is humanized ("45 min" instead of
                                "00:45:00") and labelled explicitly so it
                                no longer reads like a slot time. Date and
                                time live on separate rows; the Date row
                                uses the long "Tuesday, 26 May 2026" format
                                so staff can verify the day before booking. */}
                            <div className={s.confirmCard}>
                                <h5 className={s.confirmCardLabel}>{tl('cal.qb.service')}</h5>
                                <div className={s.confirmCardRow}>
                                    <span className={s.confirmKey}>{selectedService.name}</span>
                                    <span className={s.confirmVal}>
                                        {tl('cal.qb.duration')} {formatDurationRange(selectedService.minDuration, selectedService.maxDuration, units)}
                                        {' · '}
                                        {formatPriceRange(selectedService.pricingOptions)}
                                    </span>
                                </div>
                                <div className={s.confirmCardRow}>
                                    <span className={s.confirmKey}>{tl('cal.qb.with')}</span>
                                    <span className={s.confirmVal}>{selectedSlot.staffDisplayName}</span>
                                </div>
                                <div className={s.confirmCardRow}>
                                    <span className={s.confirmKey}>{tl('cal.qb.date')}</span>
                                    <span className={s.confirmVal}>
                                        {longDateLabel(selectedSlot.date, language)}
                                    </span>
                                </div>
                                <div className={s.confirmCardRow}>
                                    <span className={s.confirmKey}>{tl('cal.qb.time')}</span>
                                    <span className={s.confirmVal}>
                                        {selectedSlot.startTime}–{selectedSlot.endTime}
                                        {' '}
                                        ({humanizeDuration(selectedService.minDuration, units)})
                                    </span>
                                </div>
                            </div>

                            {/* AB#1849 (PBI-K) / AB#1838 — Walk-in customer
                                gender chip group. Rendered only when the
                                salon is restrictive (MenOnly / WomenOnly);
                                hidden for Everyone salons per AC-4. The
                                opposite-gender chip is greyed out + carries
                                an aria-disabled title explaining why; the
                                aligned chip is pre-selected on mount via
                                the useEffect above. The error region below
                                surfaces both client-side validation and BE
                                rejections (CUSTOMER_GENDER_REQUIRED /
                                CUSTOMER_GENDER_MISMATCHES_SALON) through a
                                single i18n-driven message. */}
                            {showCustomerGenderChips && (
                                <div
                                    className={s.customerGenderRow}
                                    role="radiogroup"
                                    aria-labelledby="quickbook-customer-gender-label"
                                    aria-required="true"
                                >
                                    <span
                                        id="quickbook-customer-gender-label"
                                        className={s.customerLabel}
                                    >
                                        {t('quickBook.customerGender.label')}
                                        {' '}
                                        <span aria-hidden="true" className={s.requiredMark}>*</span>
                                    </span>
                                    <div className={s.chipGroup}>
                                        {(['Male', 'Female'] as const).map((g) => {
                                            const isDisabled =
                                                salonRequiredGender != null && salonRequiredGender !== g;
                                            const isOn = customerGender === g;
                                            const tooltipKey = salonRequiredGender === 'Male'
                                                ? 'quickBook.customerGender.tooltip.salonMenOnly'
                                                : 'quickBook.customerGender.tooltip.salonWomenOnly';
                                            const chipClass =
                                                `${s.chip} ${isOn ? s.chipOn : ''} ${isDisabled ? s.chipDisabled : ''}`
                                                    .trim();
                                            return (
                                                <button
                                                    key={g}
                                                    type="button"
                                                    id={`quickbook-customer-gender-${g.toLowerCase()}`}
                                                    role="radio"
                                                    aria-checked={isOn}
                                                    aria-disabled={isDisabled || undefined}
                                                    title={isDisabled ? t(tooltipKey) : undefined}
                                                    className={chipClass}
                                                    onClick={() => {
                                                        if (isDisabled) return;
                                                        setCustomerGender(g);
                                                        setCustomerGenderError(null);
                                                    }}
                                                >
                                                    {g === 'Male' ? '♂ ' : '♀ '}
                                                    {t(`quickBook.customerGender.option.${g.toLowerCase()}`)}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    {customerGenderError && (
                                        <div
                                            className={s.fieldError}
                                            role="alert"
                                            id="quickbook-customer-gender-error"
                                        >
                                            {customerGenderError.key === 'customerGenderRequired'
                                                ? t('quickBook.errors.customerGenderRequired')
                                                : t('quickBook.errors.customerGenderMismatch', {
                                                    gender: t(
                                                        `quickBook.customerGender.option.${customerGenderError.params.gender.toLowerCase()}`,
                                                    ),
                                                })}
                                        </div>
                                    )}
                                </div>
                            )}

                            <div className={s.customerForm}>
                                <label className={s.customerField}>
                                    <span className={s.customerLabel}>{tl('cal.qb.customerName')}</span>
                                    <input
                                        type="text"
                                        className={s.customerInput}
                                        placeholder={tl('cal.qb.walkIn')}
                                        value={customerName}
                                        onChange={(e) => setCustomerName(e.target.value)}
                                        maxLength={120}
                                    />
                                </label>
                                <label className={s.customerField}>
                                    <span className={s.customerLabel}>{tl('cal.qb.phone')}</span>
                                    {/* AB#1796 (CR-2026-05-29) — Phone input wrapped in a
                                        relatively-positioned container so the PDPL soft-
                                        consent tooltip (rendered as a sibling) can anchor
                                        absolutely to the input without disturbing the
                                        Confirm card's existing layout / height. */}
                                    <div className={s.phoneFieldWrap}>
                                        <input
                                            type="tel"
                                            className={s.customerInput}
                                            placeholder={phonePlaceholder}
                                            value={customerPhone}
                                            aria-describedby="quickbook-phone-cue"
                                            onChange={(e) => {
                                                const next = e.target.value;
                                                const prev = customerPhone;
                                                setCustomerPhone(next);
                                                // AC-2 — Arm the soft-consent tooltip on
                                                // the FIRST non-whitespace keystroke only.
                                                // Pure walk-ins (no phone) never see it;
                                                // owners typing a phone see it once per
                                                // device (then it's persisted dismissed).
                                                const trimmedPrev = prev.trim();
                                                const trimmedNext = next.trim();
                                                if (
                                                    !phoneConsentSeenRef.current
                                                    && trimmedPrev.length === 0
                                                    && trimmedNext.length > 0
                                                ) {
                                                    setPhoneConsentTooltipOpen(true);
                                                } else if (phoneConsentTooltipOpen) {
                                                    // Any further keystroke dismisses
                                                    // (AC-2) and persists localStorage.
                                                    dismissPhoneConsentTooltip();
                                                }
                                            }}
                                            maxLength={30}
                                        />
                                        {phoneConsentTooltipOpen && (
                                            <div
                                                className={s.phoneConsentTooltip}
                                                role="tooltip"
                                                id="quickbook-phone-consent"
                                            >
                                                <span>{t('quickBook.confirm.phoneConsent')}</span>
                                                <button
                                                    type="button"
                                                    className={s.phoneConsentDismissBtn}
                                                    onClick={dismissPhoneConsentTooltip}
                                                    aria-label={t('quickBook.confirm.phoneConsentDismiss')}
                                                >
                                                    {t('quickBook.confirm.phoneConsentDismiss')}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                    {/* AC-1 — Permanent value cue. Loaded from
                                        i18n; LTR + RTL handled by CSS logical
                                        properties. NOT a tooltip — daily carrier
                                        of the SMS-reminder value prop. */}
                                    <small
                                        id="quickbook-phone-cue"
                                        className={s.phoneCue}
                                    >
                                        {t('quickBook.confirm.phoneCue')}
                                    </small>
                                </label>
                            </div>
                        </>
                    )}
                </div>

                <div className={s.footer}>
                    <button
                        type="button"
                        className={s.btnGhost}
                        onClick={step === 1 ? onClose : back}
                    >
                        {step === 1 ? tl('cal.qb.cancel') : tl('cal.qb.back')}
                    </button>

                    {/* Step 3 → "Continue to confirm →" (mockup §C line 1496) */}
                    {step === 3 && (
                        <button
                            type="button"
                            className={s.btnPrimary}
                            disabled={!selectedSlot}
                            onClick={continueToConfirm}
                        >
                            {tl('cal.qb.continue')}
                        </button>
                    )}

                    {/* Step 4 → final "Book" CTA */}
                    {step === 4 && (
                        <button
                            type="button"
                            className={s.btnPrimary}
                            disabled={bookMutation.isPending}
                            onClick={confirmBook}
                        >
                            {bookMutation.isPending ? tl('cal.qb.booking') : tl('cal.qb.book')}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default QuickBookModal;
